import React from 'react';
import {
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';

export function CommonQuestionsTab() {
  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Common Questions
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">Why should I migrate from CRA?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Create React App is no longer actively maintained by the React team. Modern alternatives like Vite and Next.js offer 
              significantly better performance, faster development experience, and more flexibility. They also provide better support 
              for modern JavaScript features and optimizations.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">Is Vite production-ready?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Yes, Vite is production-ready and is used by many large companies and projects. It's built by the Vue.js team 
              and has excellent React support. Vite uses Rollup for production builds, which is battle-tested and reliable.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">Will my existing code work with Vite?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Most React code written for CRA will work with Vite with minimal changes. The main differences are in 
              configuration and environment variables. You may need to update import paths for assets and adjust 
              some build configurations, but the React components themselves typically require no changes.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">How long does migration typically take?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              For simple projects, migration can take a few hours to a day. For complex projects with custom configurations, 
              it might take 2-5 days. The time depends on your project complexity, custom webpack configurations, 
              and third-party dependencies that might need updates.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">What about browser compatibility?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Vite targets modern browsers by default but can be configured for legacy browser support. Next.js provides 
              excellent browser compatibility out of the box. Both tools can generate builds that work with older browsers 
              when properly configured with appropriate polyfills.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">Can I migrate gradually?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              For most projects, a complete migration is recommended rather than gradual migration. Since CRA is no longer maintained, 
              staying on CRA-based tools (like CRACO) means staying on an unmaintained foundation. However, you can break down the 
              migration process into smaller steps: first migrate the build system to Vite, then gradually update your code and 
              dependencies. For monorepos, you can migrate individual packages one at a time while keeping them separate.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">What if something breaks during migration?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Always create a backup before starting migration. Use version control to track changes. Test thoroughly 
              in a development environment before deploying. Most issues are related to configuration differences 
              and can be resolved by adjusting import paths, environment variables, or build settings.
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
}
